import { z } from 'zod';

/**
 * DTO schemas describe EXACTLY what the Laravel backend returns for category
 * resources. Keep field names in snake_case so backend drift is caught at
 * parse-time rather than silently propagating into the domain.
 */
export const categoryDto = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  slug: z.string().min(1),
  name: z.string(),
  description: z.string().nullable().default(null),
  parent_id: z.union([z.string(), z.number()]).transform(String).nullable().default(null),
  image_url: z.string().url().nullable().default(null),
  icon_url: z.string().url().nullable().default(null),
  product_count: z.number().int().min(0).default(0),
  on_page: z.boolean().default(false),
});

/**
 * Recursive category node. `children` defaults to an empty list and `depth` to
 * 0 — the mapper backfills `depth` during traversal if the backend omits it.
 */
export interface CategoryNodeDto extends z.infer<typeof categoryDto> {
  readonly depth?: number | undefined;
  readonly children?: readonly CategoryNodeDto[] | undefined;
}

export const categoryNodeDto: z.ZodType<CategoryNodeDto> = categoryDto.extend({
  depth: z.number().int().min(0).optional(),
  children: z.lazy(() => z.array(categoryNodeDto).default([])),
}) as z.ZodType<CategoryNodeDto>;

export const categoryTreeDto = z.object({
  data: z.array(categoryNodeDto),
});

const breadcrumbNodeDto = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  slug: z.string().min(1),
  name: z.string(),
});

const childSummaryDto = breadcrumbNodeDto.extend({
  image_url: z.string().url().nullable().default(null),
  icon_url: z.string().url().nullable().default(null),
});

export const categoryDetailDto = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  slug: z.string().min(1),
  name: z.string(),
  description: z.string().nullable().default(null),
  parent_id: z.union([z.string(), z.number()]).transform(String).nullable().default(null),
  image_url: z.string().url().nullable().default(null),
  icon_url: z.string().url().nullable().default(null),
  on_page: z.boolean().default(false),
  ancestors: z.array(breadcrumbNodeDto).default([]),
  children: z.array(childSummaryDto).default([]),
  price_range: z
    .object({
      min: z.number().nullable().default(null),
      max: z.number().nullable().default(null),
    })
    .default({ min: null, max: null }),
});

export type CategoryDto = z.infer<typeof categoryDto>;
export type CategoryDetailDto = z.infer<typeof categoryDetailDto>;
export type CategoryTreeDto = z.infer<typeof categoryTreeDto>;
